import type { Locale } from "@/lib/i18n/config";

/**
 * apply-moment-free-cv — where the apply-moment offer sends people (2026-11-05).
 *
 * Evidence (PostHog, 60 days to 2026-11-05): `apply_offer_shown` reached 313
 * people and `apply_offer_cta_clicked` 9. That CTA opens checkout for
 * `cv_review`, a paid Rp 12k SKU with 0 purchases ever (flag-manifest
 * `free-cv-review` entry). Meanwhile the FREE review on /tools/cek-ats-cv
 * reached only 19 people (`cv_teaser_shown`) and 15 reviewers, and one of them
 * bought Pro annual 9 minutes after the review (2026-11-03 11:06
 * `cv_free_review_generated` -> 11:08 `gate_cta_clicked` -> 11:15
 * `checkout_completed`). Hypothesis: pointing the apply-moment offer at the
 * free check raises reviews per offer seen, and through them, Pro checkouts.
 *
 * `free_cv`: same overlay, copy offers the free ATS check, the CTA is a plain
 * link to /{locale}/tools/cek-ats-cv?src=apply_moment.
 * `control`, an unresolved read and any unknown value: today's paid offer,
 * byte-for-byte, and no `experiment_exposure`.
 *
 * PostHog flag "apply-moment-free-cv" (multivariate control/free_cv, 50/50) is
 * the SOLE switch for the arm; `apply-moment-cv-offer` stays the outer switch
 * for the whole overlay. Read client-side at the apply click (the job page is
 * edge-cached and per-user-free).
 */
export const APPLY_MOMENT_FREE_CV_FLAG = "apply-moment-free-cv";
export const APPLY_MOMENT_OFFER_FLAG = "apply-moment-cv-offer";

/**
 * Bounded wait for a late `/decide`. #7364: an 800 ms budget sat below real
 * PostHog latency (p50 809 ms, p90 1015 ms) and silently ran the test arm as
 * control; 2000 ms covers p90 with margin. The overlay reveals in the tab the
 * visitor left behind, so the wait never delays the apply itself.
 */
export const APPLY_MOMENT_FREE_CV_RESOLVE_TIMEOUT_MS = 2_000;

/** `src` value the free_cv CTA appends, carried through the CV tool's events. */
export const APPLY_MOMENT_ENTRY_SRC = "apply_moment";
export const CV_ENTRY_SRC_PARAM = "src";
const KNOWN_ENTRY_SRCS = new Set<string>([APPLY_MOMENT_ENTRY_SRC]);

export type ApplyMomentFreeCvArm = "control" | "free_cv";

/** Only the exact strings "control" / "free_cv" are arms. */
export function resolveApplyMomentFreeCvArm(
	raw: unknown,
): ApplyMomentFreeCvArm | null {
	return raw === "control" || raw === "free_cv" ? raw : null;
}

/** Allowlisted entry source, or null. Never echoes arbitrary input. */
export function sanitizeCvEntrySrc(raw: unknown): string | null {
	return typeof raw === "string" && KNOWN_ENTRY_SRCS.has(raw) ? raw : null;
}

export function freeCvCheckHref(locale: Locale): string {
	return `/${locale}/tools/cek-ats-cv?${CV_ENTRY_SRC_PARAM}=${APPLY_MOMENT_ENTRY_SRC}`;
}

/**
 * Non-production e2e override: the `ld_preview_force` cookie with the same
 * `flagKey=variant` syntax as src/lib/flags/preview-force.ts, read client-side
 * (see aha-signup-ask.ts for the identical two gates). posthog-js must NOT be
 * loaded (production always initialises it) and the host must not be
 * lokerdollar.com or a subdomain. Returns the raw token value or null.
 */
export function readPreviewForceToken(
	posthogLoaded: boolean,
	flagKey: string,
): string | null {
	if (posthogLoaded) return null;
	try {
		const host = window.location.hostname;
		if (host === "lokerdollar.com" || host.endsWith(".lokerdollar.com")) {
			return null;
		}
		const raw = document.cookie
			.split(";")
			.map((c) => c.trim())
			.find((c) => c.startsWith("ld_preview_force="));
		if (!raw) return null;
		const tokens = decodeURIComponent(raw.slice("ld_preview_force=".length))
			.split(",")
			.map((t) => t.trim());
		for (const token of tokens) {
			const sep = token.indexOf("=");
			if (sep <= 0 || token.slice(0, sep) !== flagKey) continue;
			return token.slice(sep + 1);
		}
	} catch {
		/* no document / cookie access: no override */
	}
	return null;
}

/** The two posthog-js methods the arm read needs (a PostHog instance fits). */
type FlagReader = {
	getFeatureFlag(key: string): unknown;
	onFeatureFlags(callback: () => void): () => void;
	__loaded?: boolean;
};

export function readApplyMomentFreeCvArmNow(
	posthog: FlagReader,
): ApplyMomentFreeCvArm | null {
	const forced = resolveApplyMomentFreeCvArm(
		readPreviewForceToken(posthog.__loaded === true, APPLY_MOMENT_FREE_CV_FLAG),
	);
	if (forced) return forced;
	return resolveApplyMomentFreeCvArm(
		posthog.getFeatureFlag(APPLY_MOMENT_FREE_CV_FLAG),
	);
}

/** Resolve the arm, waiting at most `timeoutMs` for flags. Never rejects. */
export function readApplyMomentFreeCvArm(
	posthog: FlagReader,
	timeoutMs: number = APPLY_MOMENT_FREE_CV_RESOLVE_TIMEOUT_MS,
): Promise<ApplyMomentFreeCvArm | null> {
	return new Promise((resolve) => {
		let settled = false;
		let unsubscribe: (() => void) | undefined;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const settle = (arm: ApplyMomentFreeCvArm | null) => {
			if (settled) return;
			settled = true;
			if (timer) clearTimeout(timer);
			unsubscribe?.();
			resolve(arm);
		};
		const read = () => {
			const arm = readApplyMomentFreeCvArmNow(posthog);
			if (arm) settle(arm);
		};
		read();
		if (settled) return;
		timer = setTimeout(() => settle(null), timeoutMs);
		unsubscribe = posthog.onFeatureFlags(read);
		if (settled) unsubscribe();
	});
}
